import { useState, useEffect, useRef } from 'react';
import ImagePreview from '../image-preview';
import { Link } from 'react-router-dom';
import { useNewsServices } from '../../apis/newsService';
import { FaImage, FaFilePdf } from 'react-icons/fa';
import { CardSkeleton } from '../SkeletonLoader';
import axios from 'axios';
import PageFlipViewer from '../PageFlipViewer';
import { PublicProtectedBlock } from '../PublicProtectedHtml';

const TopWeekly = () => {
  const [featuredNews, setFeaturedNews] = useState([]);
  const [displayItems, setDisplayItems] = useState([]); // News + Ads combined
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adSettings, setAdSettings] = useState(null);
  const [advertisements, setAdvertisements] = useState([]);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState({ url: null, title: null });
  
  // Use useRef to track if the initial fetch has been made
  const initialFetchDone = useRef(false);
  
  const newsServices = useNewsServices();
  const { getPublicNews } = newsServices;

  // Function to extract the first image from HTML content
  const extractFirstImageFromContent = (content) => {
    if (!content) return null;
    
    const imgRegex = /<img[^>]+src="([^">]+)"/i;
    const match = content.match(imgRegex);
    
    return match ? match[1] : null;
  };

  // Function to strip HTML tags and get plain text
  const stripHtmlTags = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/\s{2,}/g, ' ').trim();
  };

  // Calculate ad positions
  const calculateAdPositions = (contentCount, frequency) => {
    if (contentCount === 0 || frequency <= 0) return [];
    const positions = [];
    let currentPosition = frequency;
    while (currentPosition <= contentCount) {
      positions.push(currentPosition - 1); // 0-indexed
      currentPosition += frequency;
    }
    return positions;
  };

  // Insert ads into news array
  const insertAdsIntoContent = (news, ads, positions) => {
    if (!ads || ads.length === 0 || positions.length === 0) return news;
    
    const result = [...news];
    let adIndex = 0;
    
    // Insert ads at calculated positions (in reverse to maintain indices)
    positions.reverse().forEach((pos) => {
      if (pos < result.length && adIndex < ads.length) {
        result.splice(pos, 0, { type: 'advertisement', data: ads[adIndex % ads.length] });
        adIndex++;
      }
    });
    
    return result;
  };

  useEffect(() => {
    // Prevent multiple fetches
    if (initialFetchDone.current) return;
    
    const fetchFeaturedNews = async () => {
      try {
        setLoading(true);
        // Fetch trending news (up to 7 items)
        const response = await getPublicNews({ 
          isTrending: true,
          limit: 7,
          page: 1
        });
        
        if (response.success) {
          const news = response.data.news;
          setFeaturedNews(news);
          
          // Fetch ad display settings (public endpoint)
          try {
            const settingsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/advertisement-display-settings/public`);
            if (settingsResponse.data.success && settingsResponse.data.data.enabledForNews) {
              const settings = settingsResponse.data.data;
              setAdSettings(settings);
              
              // Fetch advertisements
              const adUrl = settings.useFeaturedAds 
                ? `${process.env.REACT_APP_API_URL}/api/v1/advertisements/featured`
                : `${process.env.REACT_APP_API_URL}/api/v1/advertisements/public?active=true`;
              
              const adsResponse = await axios.get(adUrl);
              if (adsResponse.data.success) {
                const ads = settings.useFeaturedAds 
                  ? adsResponse.data.data 
                  : adsResponse.data.data.advertisements || [];
                
                setAdvertisements(ads);
                
                // Calculate positions and insert ads (insert after every 2-3 news items)
                const frequency = settings.newsAdFrequency || 3;
                const positions = calculateAdPositions(news.length, frequency);
                const combined = insertAdsIntoContent(news, ads, positions);
                setDisplayItems(combined);
              } else {
                setDisplayItems(news);
              }
            } else {
              setDisplayItems(news);
            }
          } catch (adErr) {
            console.error('Error fetching ad settings:', adErr);
            setDisplayItems(news);
          }
        } else {
          setError('Failed to fetch featured news');
        }
      } catch (err) {
        console.error('Error fetching featured news:', err);
        setError('Error fetching featured news. Please try again later.');
      } finally {
        setLoading(false);
        initialFetchDone.current = true;
      }
    };

    fetchFeaturedNews();
    
    // Cleanup function to reset the ref when component unmounts
    return () => {
      initialFetchDone.current = false;
    };
  }, []); // Empty dependency array to run only once

  // Helper function to determine which image to display
  const getImageToShow = (item) => {
    if (item.imageUrl) {
      return item.imageUrl;
    } else {
      const contentImage = extractFirstImageFromContent(item.content);
      if (contentImage) {
        return contentImage;
      }
    }
    return null;
  };

  const ArticleCard = ({ article, isLarge }) => {
    const imageToShow = getImageToShow(article);
    const plainTextContent = stripHtmlTags(article.content);
    const readTime = `${Math.ceil(plainTextContent.length / 1000)} Min Read`;
    
    return (
      <div className={`mb-6 ${isLarge ? 'md:col-span-1' : ''}`}>
        <Link to={`/news-details?id=${article.id}`}>
          <PublicProtectedBlock>
            <div className="relative overflow-hidden rounded-lg bg-gray-200 flex items-center justify-center h-48">
              {imageToShow ? (
                <img 
                  src={imageToShow} 
                  alt={article.title} 
                  className="w-full h-full object-contain transition-transform duration-300 hover:scale-105" 
                  style={{
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                />
              ) : (
                <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                  <FaImage className="text-gray-400 text-4xl" />
                </div>
              )}
              <span className="absolute top-3 left-3 bg-yellow-400 text-xs font-bold px-2 py-1 rounded">
                {article.category}
              </span>
            </div>
            <h3 className="font-bold text-lg mt-3 leading-tight hover:text-blue-600">
              {article.title}
            </h3>
            <div className="flex items-center text-xs text-gray-500 mt-2">
              <span>{new Date(article.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
              <span className="mx-2">•</span>
              <span>{readTime}</span>
            </div>
          </PublicProtectedBlock>
        </Link>
      </div>
    );
  };

  const SideArticleCard = ({ article }) => {
    const imageToShow = getImageToShow(article);
    const plainTextContent = stripHtmlTags(article.content);
    const readTime = `${Math.ceil(plainTextContent.length / 1000)} Min Read`;
    
    return (
      <div className="flex flex-col mb-6">
        <Link to={`/news-details?id=${article.id}`}>
          <PublicProtectedBlock>
            <div className="relative overflow-hidden rounded-lg mb-3 bg-gray-200 flex items-center justify-center h-36">
              {imageToShow ? (
                <img 
                  src={imageToShow} 
                  alt={article.title} 
                  className="w-full h-full object-contain transition-transform duration-300 hover:scale-105" 
                  style={{
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                />
              ) : (
                <div className="w-full h-36 bg-gray-200 flex items-center justify-center">
                  <FaImage className="text-gray-400 text-3xl" />
                </div>
              )}
              <span className="absolute top-3 left-3 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">
                {article.category}
              </span>
            </div>
            <h3 className="font-bold text-base leading-tight hover:text-blue-600">
              {article.title}
            </h3>
            <div className="flex items-center text-xs text-gray-500 mt-2">
              <span>{new Date(article.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
              <span className="mx-2">•</span>
              <span>{readTime}</span>
            </div>
          </PublicProtectedBlock>
        </Link>
      </div>
    );
  };

  // Advertisement Card Component
  const AdvertisementCard = ({ ad, isSidebar = false }) => {
    const imageToShow = ad.imageUrl || extractFirstImageFromContent(ad.content);
    const plainTextContent = stripHtmlTags(ad.content);
    
    if (isSidebar) {
      // Sidebar ad - smaller format
      return (
        <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg overflow-hidden mb-6 border-2 border-orange-200">
          <PublicProtectedBlock>
            <div className="relative">
              <div className="absolute top-3 left-3 z-10 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded">
                Advertisement
              </div>
              {imageToShow && (
                <div className="relative h-36 overflow-hidden bg-gray-200 flex items-center justify-center">
                  <img 
                    src={imageToShow} 
                    alt={ad.title} 
                    className="w-full h-full object-contain"
                    style={{
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                  />
                </div>
              )}
            </div>
            <div className="p-3">
              <h3 className="font-bold text-base leading-tight mb-2 text-gray-800">{ad.title}</h3>
              <div className="flex items-center justify-between border-t pt-2 mt-2">
                <Link
                  to={`/advertisement/${ad.id}`}
                  className="text-orange-600 hover:text-orange-800 font-medium text-xs"
                >
                  View →
                </Link>
                {ad.pdfUrl && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPdf({ url: ad.pdfUrl, title: ad.title });
                      setShowPdfViewer(true);
                    }}
                    className="flex items-center text-red-600 hover:text-red-800 text-xs"
                  >
                    <FaFilePdf className="mr-1" />
                    PDF
                  </button>
                )}
              </div>
            </div>
          </PublicProtectedBlock>
        </div>
      );
    }
    
    // Main grid ad - full format
    return (
      <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg overflow-hidden mb-6 border-2 border-orange-200">
        <PublicProtectedBlock>
          <div className="relative">
            <div className="absolute top-4 left-4 z-10 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded">
              Advertisement
            </div>
            {imageToShow && (
              <div className="relative h-48 overflow-hidden bg-gray-200 flex items-center justify-center">
                <img 
                  src={imageToShow} 
                  alt={ad.title} 
                  className="w-full h-full object-contain"
                  style={{
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                />
              </div>
            )}
          </div>
          <div className="p-4">
            <h3 className="font-bold text-lg leading-tight mb-2 text-gray-800">{ad.title}</h3>
            {plainTextContent && (
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                {plainTextContent.length > 100 ? `${plainTextContent.substring(0, 100)}...` : plainTextContent}
              </p>
            )}
            <div className="flex items-center justify-between border-t pt-3 mt-2">
              <Link
                to={`/advertisement/${ad.id}`}
                className="text-orange-600 hover:text-orange-800 font-medium text-sm"
              >
                View Details →
              </Link>
              {ad.pdfUrl && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPdf({ url: ad.pdfUrl, title: ad.title });
                    setShowPdfViewer(true);
                  }}
                  className="flex items-center text-red-600 hover:text-red-800 text-sm"
                >
                  <FaFilePdf className="mr-1" />
                  PDF
                </button>
              )}
            </div>
          </div>
        </PublicProtectedBlock>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold mb-1">Top Weekly Trending Topics</h1>
          <p className="text-gray-600">Stay informed with our top weekly news articles.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CardSkeleton count={6} />
        </div>
      </div>
    );
  }

  if (error || (displayItems.length === 0 && featuredNews.length === 0)) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold mb-1">Top Weekly Trending Topics</h1>
          <p className="text-gray-600">Stay informed with our top weekly news articles.</p>
        </div>
        <div className="text-center text-gray-500">
          <p>{error || 'No featured news available at the moment.'}</p>
        </div>
      </div>
    );
  }

  // Split items (news + ads) for different sections
  // First 4 items go to main area, rest go to sidebar
  const itemsToShow = displayItems.length > 0 ? displayItems : featuredNews.map(item => ({ type: 'news', data: item }));
  const mainItems = itemsToShow.slice(0, 4);
  const sideItems = itemsToShow.slice(4);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold mb-1">Top Weekly Trending Topics</h1>
        <p className="text-gray-600">Stay informed with our top weekly news articles.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3">
          <div className="w-full text-lg font-bold mb-6 pb-2 inline-block relative">
            Featured News
            <div className="absolute flex justify-start w-full h-[4px] bg-gray-200 overflow-hidden border-[0px] border-x-0 border-gray-500 top-full">
              <div className='w-[100px] h-[100px] -translate-x-[50%] -translate-y-[60%] bg-yellow-400 transform rotate-45'></div>
              <div className='w-[10px] h-[100px] -translate-x-[400%] -translate-y-[50%] bg-blue-500 transform rotate-45'></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {mainItems.slice(0, 2).map((item, index) => {
              if (item.type === 'advertisement') {
                return <AdvertisementCard key={`ad-${item.data.id}-${index}`} ad={item.data} isSidebar={false} />;
              }
              return <ArticleCard key={item.data?.id || item.id} article={item.data || item} isLarge={true} />;
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {mainItems.slice(2, 4).map((item, index) => {
              if (item.type === 'advertisement') {
                return <AdvertisementCard key={`ad-${item.data.id}-${index}`} ad={item.data} isSidebar={false} />;
              }
              return <ArticleCard key={item.data?.id || item.id} article={item.data || item} isLarge={true} />;
            })}
          </div>
        </div>

        {sideItems.length > 0 && (
          <div>
            <h2 className="w-full text-lg font-bold mb-6 pb-2 inline-block relative">
              More Trending
              <div className="absolute flex justify-start w-full h-[4px] bg-gray-200 overflow-hidden border-[0px] border-x-0 border-gray-500 top-full">
                <div className='w-[100px] h-[100px] -translate-x-[50%] -translate-y-[60%] bg-yellow-400 transform rotate-45'></div>
                <div className='w-[10px] h-[100px] -translate-x-[400%] -translate-y-[50%] bg-blue-500 transform rotate-45'></div>
              </div>
            </h2>
            
            <div className="space-y-6">
              {sideItems.map((item, index) => {
                if (item.type === 'advertisement') {
                  return <AdvertisementCard key={`ad-${item.data.id}-${index}`} ad={item.data} isSidebar={true} />;
                }
                return <SideArticleCard key={item.data?.id || item.id} article={item.data || item} />;
              })}
            </div>
          </div>
        )}
      </div>

      {/* PDF Flipbook Viewer Modal */}
      {showPdfViewer && selectedPdf.url && (
        <PageFlipViewer
          pdfUrl={selectedPdf.url}
          title={selectedPdf.title || "PDF Document"}
          onClose={() => {
            setShowPdfViewer(false);
            setSelectedPdf({ url: null, title: null });
          }}
        />
      )}
    </div>
  );
};

export default TopWeekly;